import {
  ACCOUNT_STATE_DESCRIPTION,
  BLACKLISTED_SUSPEND_REASON_PREFIX,
  renameSpamComplaints,
  DNS_FAILURE_SUSPEND_REASON_PREFIX,
  MAILBOX_VERIFICATION_SUSPEND_REASON_PREFIX,
  SUSPEND_REASON_PARTNER_UNAVAILABLE,
  TRANSITION_TRIGGER_DESCRIPTION,
  TransitionTrigger,
} from '../enums';

/**
 * Rewrites raw warmup reason codes in notification text into the sentences the
 * account Overview shows. Idempotent, so it is safe on both write and read.
 */

// `<DIM>_<value>_PCT_<class>` from `buildThrottleReason`; a bare `SPAM_RATE` falls through.
const METRIC_REASON = /^(SPAM_RATE|BOUNCE_RATE|INBOX_PLACEMENT)_(\d+(?:\.\d+)?)_PCT_/;

const METRIC_SENTENCE: Record<string, (pct: string) => string> = {
  SPAM_RATE: (pct) => `Spam rate reached ${pct}% of sends, above the safe threshold.`,
  BOUNCE_RATE: (pct) => `Bounce rate reached ${pct}% of sends, above the safe threshold.`,
  INBOX_PLACEMENT: (pct) =>
    `Inbox placement dropped to ${pct}% of sends, below the safe threshold.`,
};

const HARD_BOUNCE_SENTENCE =
  'A warmup email to this mailbox was permanently rejected by its own mail server.';

// Exact-match codes. `USER_PAUSED` is the reason the `USER_PAUSE` trigger writes.
const EXACT_REASON: Record<string, string> = {
  USER_PAUSED: TRANSITION_TRIGGER_DESCRIPTION[TransitionTrigger.USER_PAUSE],
  DISCONNECTED: TRANSITION_TRIGGER_DESCRIPTION[TransitionTrigger.USER_DISCONNECT],
  WORKSPACE_DELETED: TRANSITION_TRIGGER_DESCRIPTION[TransitionTrigger.WORKSPACE_DELETED],
  SUBSCRIPTION_PAUSED: 'Your subscription was paused.',
  AUTH_FAILED: 'The mailbox rejected the stored login credentials.',
  SEND_FAILURES_EXCEEDED: '3 warmup sends from this mailbox failed in the last 24 hours.',
  HIGH_UNCONFIRMED_RATE: 'Too many recent sends could not be confirmed as delivered.',
  HARD_BOUNCE: HARD_BOUNCE_SENTENCE,
  [SUSPEND_REASON_PARTNER_UNAVAILABLE]:
    'Your provider callback stopped responding, so we could not refresh this mailbox’s access token.',
};

const CODE_SHAPE = /^[A-Z][A-Z0-9]*(?:[_.][A-Z0-9]+)*$/;

/** The Overview sentence for a raw warmup reason, or `null` if it is already prose. */
export function describeWarmupReason(raw: string): string | null {
  const reason = raw.trim();
  const upper = reason.toUpperCase();

  const metric = METRIC_REASON.exec(upper);
  if (metric) return METRIC_SENTENCE[metric[1]!]!(Number(metric[2]).toFixed(2));

  // `HARD_BOUNCE:<DSN phrase>` — the phrase is the evidence, quoted as-is.
  if (upper.startsWith('HARD_BOUNCE:')) {
    const detail = reason.slice('HARD_BOUNCE:'.length).trim();
    return detail ? `This mailbox rejected mail from us: "${detail}".` : HARD_BOUNCE_SENTENCE;
  }
  if (upper.startsWith(`${BLACKLISTED_SUSPEND_REASON_PREFIX}:`)) {
    const source = reason.slice(reason.indexOf(':') + 1).trim();
    return source && source.toLowerCase() !== 'unknown'
      ? `Listed on the ${source} blacklist.`
      : 'The sending domain or IP appears on a blacklist.';
  }
  if (upper.startsWith(DNS_FAILURE_SUSPEND_REASON_PREFIX)) {
    return 'A required DNS record (SPF, DKIM, or MX) is missing or misconfigured.';
  }
  if (upper.startsWith(MAILBOX_VERIFICATION_SUSPEND_REASON_PREFIX)) {
    return 'This mailbox was warming, but our test message could not be delivered to or from it.';
  }

  if (!CODE_SHAPE.test(reason)) return null;

  const known =
    EXACT_REASON[upper] ??
    (TRANSITION_TRIGGER_DESCRIPTION as Record<string, string>)[upper] ??
    (ACCOUNT_STATE_DESCRIPTION as Record<string, string>)[upper];
  if (known) return known.endsWith('.') ? known : `${known}.`;

  // Unknown code: sentence-case it and drop any numeric metric tail.
  const words = upper.replace(/_\d.*$/, '').replace(/_/g, ' ').toLowerCase();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
}

/**
 * The prose em dash. Product copy uses a spaced hyphen, so it is normalized ONCE
 * here at the public boundary rather than per lookup source — every branch, and
 * every producer added later, is covered by construction.
 *
 * Space-delimited on purpose: a BARE `—` is the deliberate empty-value
 * placeholder elsewhere in the product and must survive untouched.
 */
const PROSE_EM_DASH = / — /g;
const PROSE_EM_DASH_REPLACEMENT = ' - ';

// The pause / suspend / throttle messages end with `Reason: <reason>` (throttle
// adds a closing `.`); the re-activation message carries it in parentheses.
const REASON_AFTER_LABEL = /(Reason:\s*)(.+?)\.?$/;
const REASON_IN_PARENS = /\(([A-Z][A-Z0-9]*(?:[_.:][A-Z0-9]+)*)\)/g;
// Graduation says `MAINTENANCE mode`; recipient quarantine says `HARD bounce`.
const RAW_WORDS = /\b(MAINTENANCE|HARD|SOFT)\b(?= (?:mode|bounce)\b)/g;

/**
 * e.g. `Reason: SPAM_RATE_25.00_PCT_ESTABLISHED` → `Reason: Spam rate
 * reached 25.00% of sends, above the safe threshold.` Text without a code is unchanged.
 *
 * Also normalizes the prose em dash on the whole result — see `PROSE_EM_DASH`.
 */
export function humanizeNotificationMessage(text: string): string {
  return renameSpamComplaints(text)
    .replace(REASON_AFTER_LABEL, (match, label: string, reason: string) => {
      const sentence = describeWarmupReason(reason);
      return sentence ? `${label}${sentence}` : match;
    })
    .replace(REASON_IN_PARENS, (match, code: string) => {
      const sentence = describeWarmupReason(code);
      return sentence ? `(${sentence.replace(/\.$/, '')})` : match;
    })
    .replace(RAW_WORDS, (word: string) => word.toLowerCase())
    .replace(PROSE_EM_DASH, PROSE_EM_DASH_REPLACEMENT);
}
